import { ApiError } from '@anm/api';
import { DestinationParamsState, EditDestinationMeta } from '@anm/api/modules/stream';
import { DestinationParams, StreamRotate } from '@anm/api/modules/stream/types';
import { UploadFileProps } from '@anm/components/InputFile';
import { SelectOption } from '@anm/components/select/Select';
import { Urls } from '@anm/components/stream/destination_management/types';
import { ChangeEvent } from 'react';
import { LiveStream } from 'types/stream';

import { SwitchHandler } from '../form/Switch';

import { DestinationWithStreamMeta } from './StreamItem';

export type UploadPreviewProps = {
  onError(): void;
  onUpload(preview: string): void;
  onUploadStart(): void;
  onUploadUpdate(props: UploadUpdateProps): void;
};

export type PreviewImageProps = {
  defaultPreview: string;
  isEditDestination: boolean;
  defaultDestinationPreview?: string;
  onUploadUpdate(p: UploadUpdateProps): void;
};

type FolderPathProps = {
  path?: string;
  folderId?: number;
  onSelectPath?: () => void;
};

type StreamNameProps = {
  name: string;
  onNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

type StreamDescriptionProps = {
  description: string;
  onDescriptionChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
};

export type onDateChangeProps = {
  dates: number[];
  isDatePicker?: boolean;
};

export type StreamDateProps = {
  date: number;
  onDateChange: (props: onDateChangeProps) => void;
};

export type DestinationLimitProps = {
  maxStreamDestinations: number;
  canUseSamTypeDestination: boolean;
};

export enum StreamScreens {
  new_stream = 'new-stream',
  stream_details = 'stream-details',
  stream_destinations = 'stream-destinations'
}

export type InitialEditModalState = Pick<LiveStream, 'kind' | 'schedule'> & {
  title: string;
  preview: string;
  description: string;
  destinationParams: DestinationParams;
  folderId?: number;
  isWebinar?: boolean;
  canGuestAddDestination?: boolean;
};

type ScheduleProps = {
  isScheduled: boolean;
  toggleScheduled(): void;
};

type StreamDestinationCommonProps = {
  isStreamOwner: boolean;
  destinationsList: DestinationWithStreamMeta[];
  destinationErrors: string[];
  isDestinationLimitMessage: boolean;
  onCreateStream(preview: string): void;
  onSelectDestination(id: string): void;
  renderAddDestination?: () => JSX.Element;
};

export type Preview = {
  src: string;
  file: null | File;
};

export type PreviewSelectProps = UploadFileProps & { isEditDestination?: boolean };

type PreviewProps = {
  preview: Preview;
  onPreviewSelect(props: PreviewSelectProps): void;
};

export type UploadUpdateProps = {
  error?: ApiError;
  isPending?: boolean;
  isUploaded?: boolean;
};

type CommonProps = {
  isEdit?: boolean;
  ownerId?: string;
  isHosted?: boolean;
  isDuplicate?: boolean;
  isEditDestinations?: boolean;
};

type GuestAddDestinationProps = {
  canGuestAddDestination?: boolean;
  onAddGuestDestinationChange: SwitchHandler;
};

export type NewStreamModalProps = StreamDateProps &
  CommonProps &
  ScheduleProps &
  StreamNameProps &
  FolderPathProps &
  DestinationsListCommon &
  GuestAddDestinationProps &
  StreamDestinationCommonProps &
  StreamDescriptionProps & {
    isWebinar: boolean;
    isUploading: boolean;
    defaultPreview: string;
    isWebinarEnabled: boolean;
    isSubmitPending: boolean;
    selectedDestinationIds: string[];
    isSetupStream?: boolean;
    isClickableBackdrop?: boolean;
    isCloseButtonInsideModal?: boolean;
    initialModalState?: InitialEditModalState;
    destinationParams?: DestinationParamsState;
    isDestinationsPending?: boolean;
    onClose(): void;
    onWebinarChange(): void;
    onCreateRecording(): void;
    onUploadUpdate(p: UploadUpdateProps): void;
    onSubmitDestinationMeta(props: EditDestinationMeta): void;
    onUpdateDestinationParams(params: DestinationParams): void;
    onDeleteGuestDestination?(id: string): void;
  };

type DestinationsListCommon = {
  waveUrls: Urls;
  guestDestinationsIds: string[];
  deleteGuestDestinations: string[];
  userId?: string;
  isDestinationTabs?: boolean;
};

export type StreamDestinationStepProps = StreamDestinationCommonProps &
  DestinationsListCommon &
  CommonProps & {
    isWebinar: boolean;
    onWebinarChange(): void;
    preview: Preview;
    isPending: boolean;
    isUploading: boolean;
    isScheduled: boolean;
    selectedIds: string[];
    isWebinarEnabled: boolean;
    isDisabledSubmitButton: boolean;
    isNoDestinationsWarning: boolean;
    onBack?(): void;
    onEditDestination?(id: string): void;
    onDeleteGuestDestination?(id: string): void;
  };

export type EditStreamDestinationStepProps = CommonProps &
  Omit<StreamNameProps, 'onNameChange'> &
  Omit<StreamDescriptionProps, 'onDescriptionChange'> &
  Omit<PreviewProps, 'preview'> & {
    isPending: boolean;
    canShowPreview: boolean;
    destination: DestinationWithStreamMeta;
    privacy?: string;
    rotation?: StreamRotate;
    previewSrc?: string;
    savedPreview?: string;
    isFbInstruction?: boolean;
    editDestinationId?: string;
    onBack(): void;
    onOpenDiscardModal(): void;
    onSubmitDestinationMeta(props: EditDestinationMeta): void;
  };

export type NewStreamStepProps = CommonProps &
  FolderPathProps &
  StreamNameProps & {
    isPending: boolean;
    isRecording: boolean;
    isDisabledSubmit: boolean;
    onSubmit(): void;
    onSetupNavigate(): void;
  };

export type StreamDetailsStepProps = ScheduleProps &
  CommonProps &
  PreviewProps &
  FolderPathProps &
  StreamDateProps &
  StreamNameProps &
  StreamDescriptionProps &
  GuestAddDestinationProps &
  Omit<onDateChangeProps, 'dates'> & {
    isPending: boolean;
    isRecording: boolean;
    titleMaxLength: number;
    minsValue: SelectOption<number>;
    hoursValue: SelectOption<number>;
    minsOptions: SelectOption<number>[];
    hoursOptions: SelectOption<number>[];
    timeTypeValue: SelectOption<string>;
    timeTypeOptions: SelectOption<string>[];
    isSubmitPending: boolean;
    isDisabledSubmitButton: boolean;
    isBackButton?: boolean;
    isSetupStream?: boolean;
    destinationsList: DestinationWithStreamMeta[];
    navigateTo(screen: StreamScreens): void;
    onCreateStream(preview: string, isOpenStudio?: boolean): void;
    onTimeTypeChange(v: SelectOption<string>): void;
    handleMinsChange(v: SelectOption<number>): void;
    handleHoursChange(v: SelectOption<number>): void;
  };

export type CompareDataProps = {
  name: string;
  date: number;
  preview: Preview;
  description: string;
  isScheduled: boolean;
  fbGroupIds: string[];
  selectedDestinationIds: string[];
  folderId?: number;
  isWebinar?: boolean;
  initialModalState?: InitialEditModalState;
  destinationParams?: DestinationParamsState;
  canGuestAddDestination?: boolean;
};
